export interface Application {
  id: string;
  title: string;
  icon: string;
  tagline: string;
  description: string;
  longDescription: string;
  howItWorks: string;
  benefits: string[];
  recommendedModels: string[];
  image: string;
}

export const applications: Application[] = [
  {
    id: 'aterros-sanitarios',
    title: 'Aterros Sanitários',
    icon: 'ri-recycle-line',
    tagline: 'Contenção de lixiviados e proteção do solo e do lençol freático',
    description:
      'Impermeabilização de aterros com geomembrana PEAD para contenção de lixiviados e proteção do solo e das águas subterrâneas.',
    longDescription:
      'Em um aterro sanitário, a geomembrana de PEAD atua como barreira impermeável que impede que o chorume (lixiviado) gerado pela decomposição dos resíduos atinja o solo e o lençol freático. Ela é aplicada na base, nas laterais e na cobertura final das células de disposição de resíduos, formando um sistema de impermeabilização contínuo e de alta durabilidade. A soldagem das mantas com cunha quente automática garante emendas estanques e com canal de teste de pressão, atendendo aos requisitos da norma NBR 16199.',
    howItWorks:
      'As mantas de PEAD são desenroladas sobre o solo preparado e sobrepostas em 7 a 15 cm. As emendas são soldadas com cunha quente automática, criando uma solda dupla com canal de teste de pressão. Cantos, junções e reparos são executados com extrusora, garantindo a continuidade da barreira impermeável em toda a área.',
    benefits: [
      'Impede a contaminação do solo e do lençol freático pelo chorume',
      'Solda dupla com canal de teste de pressão (NBR 16199)',
      'Alta resistência química aos lixiviados agressivos',
      'Durabilidade superior a 30 anos quando bem executada',
    ],
    recommendedModels: ['bt950', 'cx500l', 'weldy-wgw300-plus', 'extrusora-ex3'],
    image:
      'https://readdy.ai/api/search-image?query=Modern%20sanitary%20landfill%20site%20with%20geomembrane%20liner%20installation%20under%20construction%2C%20heavy%20machinery%20working%20on%20waste%20containment%20area%2C%20industrial%20environmental%20engineering%20project%2C%20warm%20daylight%20lighting%2C%20professional%20documentary%20photography&width=600&height=400&seq=app-aterro&orientation=landscape',
  },
  {
    id: 'mineracao',
    title: 'Mineração',
    icon: 'ri-hammer-line',
    tagline: 'Pilhas de lixiviação, bacias de contenção e barragens de rejeitos',
    description:
      'Revestimento de pilhas de lixiviação, bacias de contenção e barragens de rejeitos com geomembrana de alta resistência química.',
    longDescription:
      'Na mineração, a geomembrana PEAD é utilizada para impermeabilizar pilhas de lixiviação (leach pads), onde soluções químicas extraem os metais do minério, além de bacias de contenção, canais e barragens de rejeitos. O material precisa resistir a substâncias agressivas, como ácidos e cianeto, e a condições de trabalho extremas. Máquinas de alta potência e velocidade garantem a produtividade exigida por obras de grande porte.',
    howItWorks:
      'Mantas espessas de PEAD (geralmente de 1,5 a 2,0 mm) são soldadas sobre áreas extensas com cunha quente de alta velocidade. As emendas recebem teste de pressão de ar e, nos detalhes, utiliza-se extrusão. O resultado é uma barreira contínua que protege o meio ambiente e viabiliza a recuperação dos metais.',
    benefits: [
      'Resistência a ácidos, cianeto e soluções de processo agressivas',
      'Alta velocidade de soldagem para grandes áreas',
      'Soldas de alta resistência mecânica e química',
      'Atende aos padrões ambientais da indústria mineral',
    ],
    recommendedModels: ['cx500l', 'bt-gm1', 'weldy-wgw300-plus', 'extrusora-ex3'],
    image:
      'https://readdy.ai/api/search-image?query=Large%20mining%20leach%20pad%20with%20geomembrane%20liner%20covering%2C%20copper%20or%20gold%20extraction%20facility%2C%20industrial%20earthworks%20and%20excavation%20in%20open%20pit%20mine%2C%20clear%20sky%2C%20professional%20industrial%20photography&width=600&height=400&seq=app-mineracao&orientation=landscape',
  },
  {
    id: 'lagoas',
    title: 'Lagoas e Reservatórios',
    icon: 'ri-water-flash-line',
    tagline: 'Armazenamento de água sem perdas por infiltração',
    description:
      'Construção de reservatórios de água, lagoas de retenção e barragens com selo hermético e longa vida útil.',
    longDescription:
      'Lagoas e reservatórios impermeabilizados com geomembrana garantem o armazenamento de água sem perdas por infiltração no solo. A aplicação é comum em reservatórios de água potável, lagoas de decantação, bacias de retenção de águas pluviais e barragens de pequeno e médio porte. A geomembrana forma uma barreira contínua que mantém o nível da água e evita a contaminação do subsolo.',
    howItWorks:
      'A geomembrana é instalada sobre o talude e o fundo da lagoa, com emendas soldadas por cunha quente. A solda dupla com canal de teste permite verificar a estanqueidade de cada emenda antes do enchimento. O acabamento nas bordas e emendas de detalhe é feito com extrusora.',
    benefits: [
      'Elimina perdas de água por infiltração',
      'Aumenta a vida útil do reservatório',
      'Rápida instalação em comparação a outros sistemas',
      'Emendas testáveis por pressão de ar',
    ],
    recommendedModels: ['bt1600', 'cx300-1400w', 'bt950'],
    image:
      'https://readdy.ai/api/search-image?query=Water%20reservoir%20with%20blue%20HDPE%20geomembrane%20liner%20being%20installed%2C%20large%20artificial%20pond%20construction%2C%20irrigation%20or%20drinking%20water%20storage%20project%2C%20clear%20blue%20water%20reflection%2C%20professional%20engineering%20photography&width=600&height=400&seq=app-lagoa&orientation=landscape',
  },
  {
    id: 'piscicultura',
    title: 'Piscicultura',
    icon: 'ri-drop-line',
    tagline: 'Tanques de criação de peixes e camarões',
    description:
      'Tanques de criação de peixes e camarões com geomembrana para retenção de água e controle sanitário.',
    longDescription:
      'Na piscicultura, os tanques revestidos com geomembrana de PEAD ou LLDPE oferecem controle sanitário, retenção de água e facilidade de limpeza. A superfície lisa da geomembrana impede o acúmulo de resíduos e parasitas no fundo, melhorando a qualidade da água e a saúde dos animais. É amplamente utilizada na criação de tilápia, tambaqui, camarão e outras espécies.',
    howItWorks:
      'Os tanques são escavados e revestidos com geomembrana, com emendas soldadas por cunha quente ou por extrusão nos cantos. A barreira impermeável mantém o volume de água estável e permite a desinfecção completa entre os ciclos de produção.',
    benefits: [
      'Controle sanitário e prevenção de doenças',
      'Retenção de água sem perdas por infiltração',
      'Facilidade de limpeza e desinfecção entre ciclos',
      'Superfície lisa que não danifica os peixes',
    ],
    recommendedModels: ['bt800', 'bt1600', 'extrusora-ex3'],
    image:
      'https://readdy.ai/api/search-image?query=Aquaculture%20fish%20farm%20with%20geomembrane%20lined%20tanks%2C%20tilapia%20or%20shrimp%20cultivation%20facility%2C%20organized%20water%20ponds%20with%20aeration%20system%2C%20tropical%20environment%2C%20professional%20agricultural%20photography&width=600&height=400&seq=app-piscicultura&orientation=landscape',
  },
  {
    id: 'saneamento',
    title: 'Saneamento',
    icon: 'ri-flask-line',
    tagline: 'Lagoas de tratamento de efluentes e saneamento básico',
    description:
      'Impermeabilização de lagoas de tratamento de efluentes e sistemas de saneamento básico.',
    longDescription:
      'No saneamento, a geomembrana é utilizada em lagoas de estabilização, lagoas aeradas, bacias de equalização e estações de tratamento de efluentes. A impermeabilização evita que os efluentes contaminem o solo e o lençol freático, além de garantir o controle do processo de tratamento. O material deve resistir a efluentes domésticos e industriais com diferentes graus de agressividade.',
    howItWorks:
      'As lagoas são revestidas com geomembrana PEAD, com emendas soldadas por cunha quente nos trechos planos e extrusão nos cantos e passagens de tubulação. A solda dupla com canal de teste assegura a estanqueidade do sistema.',
    benefits: [
      'Protege o solo e o lençol freático de efluentes',
      'Resistência a efluentes domésticos e industriais',
      'Controle do processo de tratamento',
      'Emendas estanques e testáveis',
    ],
    recommendedModels: ['cx300-1400w', 'bt-gm1', 'extrusora-metabo-bt610a'],
    image:
      'https://readdy.ai/api/search-image?query=Wastewater%20treatment%20facility%20with%20geomembrane%20covered%20lagoons%2C%20sewage%20treatment%20plant%20construction%2C%20environmental%20sanitation%20infrastructure%2C%20industrial%20water%20management%2C%20professional%20civil%20engineering%20photography&width=600&height=400&seq=app-saneamento&orientation=landscape',
  },
  {
    id: 'agricultura',
    title: 'Agricultura e Irrigação',
    icon: 'ri-plant-line',
    tagline: 'Canais de irrigação e reservatórios rurais',
    description:
      'Canais de irrigação, silos e reservatórios rurais com geomembrana para máxima eficiência hídrica.',
    longDescription:
      'Na agricultura, a geomembrana é utilizada para impermeabilizar canais de irrigação, reservatórios de água, silos de armazenamento e açudes. A aplicação reduz as perdas de água por infiltração, aumentando a eficiência do sistema de irrigação e garantindo o abastecimento hídrico em períodos de seca. É uma solução econômica e durável para propriedades rurais de todos os tamanhos.',
    howItWorks:
      'Canais e reservatórios são revestidos com geomembrana, com emendas soldadas por cunha quente. Em áreas de menor porte e cantos, utiliza-se o soprador térmico BT2000D com rolo de silicone e a extrusora para os acabamentos.',
    benefits: [
      'Reduz as perdas de água na irrigação',
      'Garante reserva hídrica para períodos de seca',
      'Solução econômica para propriedades rurais',
      'Instalação rápida e baixa manutenção',
    ],
    recommendedModels: ['bt800', 'cx300-1400w', 'bt2000d'],
    image:
      'https://readdy.ai/api/search-image?query=Agricultural%20irrigation%20canal%20with%20geomembrane%20liner%2C%20rural%20farming%20landscape%2C%20water%20delivery%20system%20for%20crop%20cultivation%2C%20green%20fields%20and%20organized%20water%20channels%2C%20sunny%20day%2C%20professional%20agricultural%20photography&width=600&height=400&seq=app-agricultura&orientation=landscape',
  },
  {
    id: 'lonas-de-pvc',
    title: 'Lonas de PVC',
    icon: 'ri-umbrella-line',
    tagline: 'Soldagem de lonas, banners, toldos e coberturas',
    description:
      'Soldagem de lona de PVC para banners, toldos, coberturas e estruturas tensionadas — não indicada para geomembrana.',
    longDescription:
      'A soldagem de lona de PVC é uma aplicação diferente da geomembrana. Enquanto a geomembrana impermeabiliza aterros, barragens e reservatórios, a lona de PVC é utilizada em banners publicitários, toldos, coberturas, lonas de caminhão e estruturas tensionadas. Para essa aplicação, o equipamento correto é a LC3000C, uma máquina de ar quente de alta pressão específica para lona de PVC, que NÃO deve ser utilizada em geomembrana.',
    howItWorks:
      'As emendas de lona de PVC são seladas com a máquina de ar quente de alta pressão LC3000C, que funde as duas bordas sobrepostas criando uma emenda resistente e estanque. Para acabamentos e pequenos trechos, utiliza-se o soprador térmico BT2000D com rolo de silicone.',
    benefits: [
      'Emendas resistentes e estanques em lona de PVC',
      'Equipamento específico para banners, toldos e coberturas',
      'Acabamento profissional e uniforme',
      'Adequada para espessuras de 400 a 1100 µm',
    ],
    recommendedModels: ['lc3000c', 'bt2000d'],
    image:
      'https://readdy.ai/api/search-image?query=Industrial%20PVC%20tarpaulin%20welding%20workshop%20with%20large%20white%20and%20colored%20PVC%20banners%20and%20truck%20tarps%20being%20sealed%20with%20hot%20wedge%20welding%20machine%2C%20rolled%20tarpaulin%20fabric%20rolls%20in%20background%2C%20clean%20bright%20industrial%20lighting%2C%20professional%20product%20photography&width=600&height=400&seq=app-lona-pvc&orientation=landscape',
  },
];